// src/pages/Users/Galleries/AddPhotoToAlbumModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes, FaUpload } from "react-icons/fa";
import Button from "../../../components/Button/Button";

// Métadonnées (description / is_active)
import { addPhoto, updatePhoto, deletePhoto } from "../../../services/photoServices";

// ✅ Upload 100% serveur (/uploads/photos_album/:gallerie_id)
import {
  addAlbumPhoto,
  replaceAlbumPhoto,
  toAbsolutePhotoUrl as toAbsPhoto,
} from "../../../services/albumPhotoServices";

import { toast } from "react-toastify";

const FALLBACK = "/img/no-image.jpg";

export default function AddPhotoToAlbumModal({ isOpen, onClose, galleryId, photoId }) {
  const [currentId, setCurrentId] = useState(photoId ?? null); // id réel de la photo
  const isEdit = Boolean(currentId);

  // form
  const [photoUrl, setPhotoUrl] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  // upload
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // ui
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const firstFieldRef = useRef(null);

  const initial = useMemo(
    () => ({ photoUrl: "", description: "", isActive: true }),
    []
  );

  // lock scroll + esc + focus
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => {
      window.removeEventListener("keydown", onKey);
      root.style.overflow = prevOverflow;
      clearTimeout(t);
    };
    // eslint-disable-next-line
  }, [isOpen]);

  useEffect(() => {
    setCurrentId(photoId ?? null);
  }, [photoId]);

  const isDirty =
    photoUrl !== initial.photoUrl ||
    description !== initial.description ||
    isActive !== initial.isActive ||
    !!selectedFile;

  const onImgError = (e) => { if (e?.currentTarget) e.currentTarget.src = FALLBACK; };

  // Aperçu : object URL si fichier local, sinon URL absolue serveur
  const previewURL = useMemo(() => {
    if (selectedFile) return URL.createObjectURL(selectedFile);
    return toAbsPhoto(photoUrl) || "";
  }, [selectedFile, photoUrl]);

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith("image/")) {
      setSelectedFile(f);
    } else {
      toast.warn("Dépose une image valide (JPG, PNG, WebP…).");
    }
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  // Étape 1 — UPLOAD côté serveur
  const handleUploadImage = async () => {
    if (!selectedFile) {
      toast.warn("Choisis une image avant de téléverser.");
      return;
    }
    try {
      setUploading(true);

      // Création + upload en UNE passe si pas encore d'ID
      if (!currentId) {
        const created = await addAlbumPhoto(galleryId, selectedFile, {
          format: "webp",
          description,      // on peut déjà pousser la légende
          is_active: isActive,
        });
        setCurrentId(created?.id || null);
        setPhotoUrl(created?.photo_url || "");
        setSelectedFile(null);
        toast.success("Image téléversée sur le serveur.");
        return;
      }

      // Sinon, remplacement du fichier existant
      const updated = await replaceAlbumPhoto(galleryId, currentId, selectedFile, { format: "webp" });
      setPhotoUrl(updated?.photo_url || "");
      setSelectedFile(null);
      toast.success("Image remplacée sur le serveur.");
    } catch (err) {
      console.error(err);
      toast.error("Échec du téléversement de l’image.");
    } finally {
      setUploading(false);
    }
  };

  // Étape 2 — ENREGISTRER/mettre à jour les infos (description / actif)
  const handleSaveInfos = async () => {
    // Si pas encore d’ID (aucune image envoyée), on crée une entrée “métadonnées only”
    if (!currentId) {
      try {
        setSaving(true);
        const created = await addPhoto({ gallerie_id: galleryId, description, is_active: isActive });
        setCurrentId(created?.id || null);
        setPhotoUrl(created?.photo_url || ""); // souvent vide si pas d'image
        toast.success("Infos enregistrées. Téléverse l’image pour finaliser.");
      } catch (err) {
        console.error(err);
        toast.error("Échec de la création de la photo.");
      } finally {
        setSaving(false);
      }
      return;
    }

    try {
      setSaving(true);
      await updatePhoto(currentId, { gallerie_id: galleryId, description, is_active: isActive });
      toast.success("Infos mises à jour.");
    } catch (err) {
      console.error(err);
      toast.error("Échec de la sauvegarde des infos.");
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = () => { onClose?.(); };

  const handleClose = async () => {
    const noImage = !photoUrl && !selectedFile;
    const noDesc = !description.trim();
    if (currentId && noImage && noDesc) {
      const ok = window.confirm("Annuler et supprimer l’entrée vide ?");
      if (ok) {
        try { await deletePhoto(currentId); toast.success("Photo supprimée."); }
        catch { toast.error("Échec de la suppression."); }
      }
    }
    onClose?.();
  };

  return isOpen
    ? ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-3 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={isEdit ? "Éditer une photo" : "Ajouter une photo"}
          onClick={(e) => e.currentTarget === e.target && handleClose()}
        >
          <div
            className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white flex items-center justify-between">
              <h3 className="font-semibold">{isEdit ? "Éditer la photo" : "Ajouter une photo"}</h3>
              <button
                onClick={handleClose}
                title="Fermer"
                aria-label="Fermer"
                className="p-2 rounded-full hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70"
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-6 space-y-4">
              {/* Dropzone + Aperçu */}
              <div
                onDragOver={onDragOver}
                onDrop={onDrop}
                className="rounded-xl overflow-hidden ring-1 ring-dashed ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-900"
              >
                <div className="w-full bg-slate-50 dark:bg-slate-800" style={{ aspectRatio: "16 / 9" }}>
                  <img
                    src={previewURL || FALLBACK}
                    alt="Aperçu"
                    className="w-full h-full object-contain"
                    onError={onImgError}
                  />
                </div>
                <div className="p-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div className="text-xs text-slate-600 dark:text-slate-300">
                    Dépose une image ou{" "}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="underline font-semibold text-sky-700 dark:text-sky-400"
                    >
                      sélectionne un fichier
                    </button>
                    .
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f && f.type.startsWith("image/")) setSelectedFile(f);
                      }}
                    />
                    <Button
                      text={uploading ? "Téléversement…" : "Téléverser l’image"}
                      icon={FaUpload}
                      onClick={handleUploadImage}
                      className="rounded-xl gap-2"
                      disabled={uploading || !selectedFile}
                    />
                  </div>
                </div>
              </div>

              {/* URL (readonly) — toujours côté serveur */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  URL de l’image (serveur)
                </label>
                <input
                  ref={firstFieldRef}
                  type="text"
                  value={toAbsPhoto(photoUrl)}
                  readOnly
                  placeholder="Visible après téléversement"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Légende / Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Ex. Podium Minimes — Merci aux bénévoles !"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>

              {/* Switch actif */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-slate-800 dark:text-slate-100">Visibilité</label>
                <button
                  type="button"
                  onClick={() => setIsActive((v) => !v)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition
                    ${isActive ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"}`}
                  aria-pressed={isActive}
                  aria-label="Basculer l’état actif"
                  title={isActive ? "Actif" : "Inactif"}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition
                      ${isActive ? "translate-x-5" : "translate-x-1"}`} />
                </button>
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  {isActive ? "Photo visible" : "Photo masquée"}
                </span>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-2">
                <Button
                  text="Enregistrer les infos"
                  icon={FaSave}
                  onClick={handleSaveInfos}
                  className="gap-2 rounded-xl"
                  disabled={saving}
                />
                <Button
                  text="Terminer"
                  onClick={handleFinish}
                  icon={FaTimes}
                  className="gap-2 rounded-xl"
                />
              </div>

              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                Téléverse d’abord l’image (serveur), puis enregistre la légende. La fenêtre reste ouverte pour vérification.
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;
}
